import { ManagedSelect } from "components/orders/Fields/Select/ManagedSelect";
import { ManagedToggle } from "components/orders/Fields/Toggle/ManagedToggle";
import React, { useEffect, useState } from "react";
import type { DeckFormType } from "./DeckSchema";
import "./Deck.scss";
import type { AnchorsDeckValidity } from "../AnchorsForm/AnchorsForm";
import {
  deckingTypeOptions,
  hardwareTypeOptions,
  paddingTypeOptions,
  DECKING_SPA,
  DECKING_RAISED_WALL,
  DECKING_SPA_OPTION,
  DECKING_RAISED_WALL_OPTION,
} from "./DeckConstants";
import type { FeatureMeasurements } from "models/Project";
import { useT } from "i18n";
import type { BaseDeckFormType } from "../../Features/Feature/FeatureSchema";
import { OrderContext } from "context/OrderContext";
import { FeatureType } from "models/Feature";
import { resolveSuggestedRDMResult, type RDMResult } from "./helpers";
import { anchorTypeOptions, ANCHOR_NA } from "../../Features/Feature/FeatureConstants";
import StrapLengthSlider from "./StrapLengthSlider/StrapLengthSlider/StrapLengthSlider";
// Constants
const DEFAULT_STRAP_LENGTH_IN = 18;
const STRAP_LENGTH_MIN_IN = 18;
const STRAP_LENGTH_MAX_IN = 48;
const STRAP_LENGTH_STEP_IN = 2;

const deckTypeLabelCopy = "Decking Type";
const hardwareLabelCopy = "Hardware";
const brassCollarsCopy = "Include Brass Collars";
const paddingCopy = "Is the coping rough / would you like additional perimeter padding";
const paddingTypeCopy = "Padding Type";
const reducedDeckMountsTitleCopy = "Reduced Deck Mounts";
const eligibleCopy = "Eligible";
const noImageFoundCopy = "No image found.";

interface GetHardwareOptionsArgs {
  deckingType: string | undefined;
  isRDM: boolean;
  isWall: boolean;
  isSpa: boolean;
  isSpaCovered?: boolean; // Only relevant when isSpa is true
}

/**
 * Gets the appropriate hardware options based on decking type and context
 * @param args - Configuration object containing decking type and context flags
 */
const getHardwareOptions = (
  args: GetHardwareOptionsArgs,
): Array<{ label: string; value: string }> => {
  const { deckingType, isRDM, isWall, isSpa, isSpaCovered } = args;

  // When RDM is selected, always use standard hardware options based on decking type
  if (isRDM) {
    if (deckingType && deckingType in hardwareTypeOptions) {
      return hardwareTypeOptions[deckingType as keyof typeof hardwareTypeOptions];
    }
    return [];
  }

  // When RDM is NOT selected (user override case):
  // - Walls always use anchorTypeOptions
  // - Spas use Concrete options if covered, otherwise anchorTypeOptions
  // - Other decking types use standard options
  if (isWall) {
    return anchorTypeOptions.filter((option) => option.value !== ANCHOR_NA);
  }

  if (isSpa) {
    return isSpaCovered
      ? hardwareTypeOptions["Concrete"]
      : anchorTypeOptions.filter((option) => option.value !== ANCHOR_NA);
  }

  if (deckingType && deckingType in hardwareTypeOptions) {
    return hardwareTypeOptions[deckingType as keyof typeof hardwareTypeOptions];
  }

  return [];
};

interface DeckProps {
  title: string;
  showHardwareDropdown: boolean;
  image?: string;
  notes?: string;
  setDeck: (deck: DeckFormType) => void;
  validFields: AnchorsDeckValidity;
  deck: DeckFormType;
  length: number;
  archived?: boolean;
  featureName: string | null | undefined;
  measurement?: FeatureMeasurements;
  features?: BaseDeckFormType[];
}

const Deck = ({
  title,
  showHardwareDropdown,
  image,
  notes,
  setDeck,
  validFields,
  deck,
  length,
  archived,
  featureName,
  measurement,
  features,
}: DeckProps) => {
  const t = useT();
  const translateDeckOptionLabel = (value: string, label: string): string => {
    const translationMap: Record<string, string> = {
      Concrete: "component.deckConstants.concrete",
      "Pavers with a concrete base": "component.deckConstants.paversConcrete",
      "Pavers with a sand base": "component.deckConstants.paversSand",
      "Grass or Dirt": "component.deckConstants.grassDirt",
      "Wood/Composite": "component.deckConstants.woodComposite",
      "Standard Brass Anchors": "component.deckConstants.standardBrassAnchors",
      '2" Long Brass Anchors': "component.deckConstants.twoInchBrassAnchors",
      "Deck Tubes": "component.deckConstants.deckTubes",
      "Lawn Tubes": "component.deckConstants.lawnTubes",
      "Wood Deck Anchors": "component.deckConstants.woodDeckAnchors",
      "Sewn On": "component.deckConstants.sewnOn",
      Replaceable: "component.deckConstants.replaceable",
      'Loose (2" X 20")': "component.deckConstants.loose",
    };
    const translationKey = translationMap[value];
    return translationKey ? t(translationKey, label) : label;
  };

  const translateDeckOptions = (
    options: Array<{ label: string; value: string }>,
  ): Array<{ label: string; value: string }> => {
    return options.map((option) => ({
      ...option,
      label: translateDeckOptionLabel(option.value, option.label),
    }));
  };

  if (typeof deck?.decking === undefined) {
    throw new Error("Decks are required for deck component to render!");
  }

  const { state } = React.useContext(OrderContext);

  const coverOverlapIn = state.project?.coverOverlap ?? 0;
  const deckingProperties = measurement?.deckingProperties ?? [];

  const isRaisedWall =
    coverOverlapIn >= 12 &&
    (deckingProperties?.[0]?.numPoints ?? 0) > 0 &&
    (deckingProperties?.[0]?.lengthAlongPerimeterIn ?? 0) > 0;
  const isRDMBucket2 =
    coverOverlapIn >= 18 &&
    (deckingProperties?.[1]?.numPoints ?? 0) > 0 &&
    (deckingProperties?.[1]?.lengthAlongPerimeterIn ?? 0) > 0;
  const isRDMBucket3 =
    coverOverlapIn >= 24 &&
    (deckingProperties?.[2]?.numPoints ?? 0) > 0 &&
    (deckingProperties?.[2]?.lengthAlongPerimeterIn ?? 0) > 0;
  const isDeckRDMEligible = isRaisedWall || isRDMBucket2 || isRDMBucket3;

  const [isRDM, setIsRDM] = useState<boolean>(
    isDeckRDMEligible ? (deck?.rdm ?? false) : false,
  );

  const [suggestedRDM, setSuggestedRDM] = useState<RDMResult>(
    (deck?.suggestedRDM as RDMResult) ?? "NO",
  );

  // Field states
  const [deckingType, setDeckingType] = useState<string | undefined>(
    deck?.decking ?? undefined,
  );
  const [hardwareOption, setHardwareOption] = useState<string | undefined>(
    deck?.hardware ?? undefined,
  );
  const [includeBrassCollars, setIncludeBrassCollars] = useState(
    deck?.collars ?? undefined,
  );
  const [isRough, setIsRough] = useState(deck?.padding ?? false);
  const [paddingType, setPaddingType] = useState<string | undefined>(
    deck?.paddingType ?? undefined,
  );
  const [strapLength, setStrapLength] = useState<number>(
    deck?.strapLength ?? DEFAULT_STRAP_LENGTH_IN,
  );

  useEffect(() => {
    if (!showHardwareDropdown && !isRDM) {
      setHardwareOption(undefined);
      setIncludeBrassCollars(undefined);
    }
  }, [showHardwareDropdown, isRDM]);

  // Auto-set RDM based on order status:
  const isNewOrder = state.order?.status === undefined;
  const distanceFromPerimeterIn = measurement?.distanceFromPerimeterIn ?? null;

  const featuresIncludeSpa = features?.some(
    (feature) => feature.featureType === FeatureType.Spa,
  );
  const featuresIncludeWall = features?.some(
    (feature) => feature.featureType === FeatureType.Wall,
  );

  const isSpa = deckingType === DECKING_SPA;
  const isWall = deckingType === DECKING_RAISED_WALL;

  const hasRaisedWallBehindDeck =
    Boolean(distanceFromPerimeterIn) && (featuresIncludeWall || featuresIncludeSpa);
  const poolMeasurementsSuggestRDM =
    suggestedRDM === "YES" || suggestedRDM === "DOUBLE";

  // Calculate suggested RDM based on pool measurements
  useEffect(() => {
    if (
      !state.project?.poolShapeType ||
      !state.project?.coverAreaSqFt ||
      !state.project?.coverOverlap ||
      !distanceFromPerimeterIn
    ) {
      return;
    }
    const suggestedResults = resolveSuggestedRDMResult({
      poolShapeType: state.project?.poolShapeType,
      coverAreaSqFt: state.project?.coverAreaSqFt,
      coverOverlapIn: state.project?.coverOverlap,
      distanceFromPerimeterIn,
      hasRaisedWallBehindDeck,
    });
    // resolveSuggestedRDMResult always returns a value (defaults to 'NO')
    setSuggestedRDM(suggestedResults);
    setIsRDM(poolMeasurementsSuggestRDM);
  }, [
    isNewOrder,
    deck?.suggestedRDM,
    state.project?.poolShapeType,
    state.project?.coverAreaSqFt,
    state.project?.coverOverlap,
    distanceFromPerimeterIn,
    hasRaisedWallBehindDeck,
  ]);

  const doesPoolMeasurementSuggestRDM =
    suggestedRDM === "YES" || suggestedRDM === "DOUBLE";

  const didUserOverrideRDM = doesPoolMeasurementSuggestRDM && !isRDM;

  // Clear strapLength when didUserOverrideRDM becomes false
  useEffect(() => {
    if (!didUserOverrideRDM) {
      setStrapLength(DEFAULT_STRAP_LENGTH_IN);
    }
  }, [didUserOverrideRDM]);

  // Update parent component whenever deck data changes
  useEffect(() => {
    setDeck({
      decking: deckingType,
      hardware: hardwareOption,
      padding: isRough,
      collars: includeBrassCollars,
      rdm: isRDM,
      suggestedRDM,
      paddingType,
      length,
      featureName,
      notes,
      ...(didUserOverrideRDM ? { strapLength } : {}), // Only include strapLength if didUserOverrideRDM is true
    } as DeckFormType);
  }, [
    deckingType,
    hardwareOption,
    isRough,
    isRDM,
    paddingType,
    includeBrassCollars,
    suggestedRDM,
    strapLength,
    didUserOverrideRDM,
    length,
    featureName,
    notes,
  ]);

  useEffect(() => {
    // Clear paddingType if isRough toggle switches to "off"
    if (!isRough) setPaddingType(undefined);
  }, [isRough]);

  const handleRDMToggle = () => {
    // Reset form fields when RDM toggle is switched
    setDeckingType(undefined);
    setHardwareOption(undefined);
    setIncludeBrassCollars(undefined);
    setIsRDM(!isRDM);
  };

  const hardwareOptions = getHardwareOptions({
    deckingType,
    isRDM,
    isWall,
    isSpa,
    // Only pass isSpaCovered when isSpa is true (a spa can be covered or uncovered)
    isSpaCovered: isSpa ? deck?.isCovered : undefined,
  });

  const renderHardwareSelect = () => (
    <ManagedSelect
      label={hardwareLabelCopy}
      isValid={validFields.hardware}
      value={hardwareOption}
      onChange={setHardwareOption}
      name={"hardware-type"}
      options={hardwareOptions}
      disabled={archived}
    />
  );

  const renderSpaOrWallWarning = () => {
    if (!isSpa && !isWall) return null;

    let message: string;
    if (isWall) {
      message = "Thunderbolt anchors are required for Raised Walls";
    } else if (deck?.isCovered) {
      message = "Standard anchors are required for covered Spas";
    } else {
      message = "Thunderbolt anchors are required for uncovered Spas";
    }

    return (
      <span className={"featureContainer"}>
        <i className={"featureIcon info circle icon"} />
        <div className={"featureWarning"}>{message}</div>
      </span>
    );
  };

  const renderHardwareSection = () => {
    if (!showHardwareDropdown) return null;

    // Case 1: Pool suggests RDM
    if (doesPoolMeasurementSuggestRDM) {
      // User selected RDM
      if (isRDM) {
        return renderHardwareSelect();
      }

      // User overrode RDM (selected NO when suggested YES)
      // Show special hardware options for Spa/Wall, or standard for other decking types
      if (isWall || isSpa) {
        return (
          <>
            {renderHardwareSelect()}
            <StrapLengthSlider
              min={STRAP_LENGTH_MIN_IN}
              max={STRAP_LENGTH_MAX_IN}
              step={STRAP_LENGTH_STEP_IN}
              value={strapLength}
              onChange={setStrapLength}
            />
          </>
        );
      }

      return renderHardwareSelect();
    }

    // Case 2: Pool does not suggest RDM - show standard hardware options
    return renderHardwareSelect();
  };

  return (
    <div className={"deck"}>
      <div className={"deck__title"}>{title}</div>

      {image ? (
        <img className={"deck__image"} src={image} />
      ) : (
        <div>{noImageFoundCopy}</div>
      )}
      {notes && <div>{notes}</div>}

      {isDeckRDMEligible ? (
        <>
          <div className={"rdm-card__header"}>
            <h3 className={"rdm-card__title"}>{reducedDeckMountsTitleCopy}</h3>
            <span className={"rdm-card__badge"} aria-label={eligibleCopy}>
              {eligibleCopy}
            </span>
          </div>
          <div className={"rdm-card"}>
            <div
              className={"rdm-card__message-container"}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <span className={"featureContainer"}>
                <i className={"featureIcon info circle icon"} />
                <div className={"featureWarning"}>
                  {poolMeasurementsSuggestRDM
                    ? "Based on this pools measurements, RDM is suggested."
                    : "Based on this pools measurements, RDM is not suggested."}
                </div>
              </span>
            </div>
            <ManagedToggle
              label={"Add Reduced Deck Mounts to this deck?"}
              name={"rdm"}
              isValid={validFields.rdm}
              onChange={handleRDMToggle}
              checked={isRDM}
              disabled={archived}
            />
          </div>

          <ManagedSelect
            label={deckTypeLabelCopy}
            isValid={validFields.decking}
            value={deckingType}
            onChange={setDeckingType}
            options={
              didUserOverrideRDM
                ? [
                    ...deckingTypeOptions,
                    ...(featuresIncludeSpa ? [DECKING_SPA_OPTION] : []),
                    ...(featuresIncludeWall ? [DECKING_RAISED_WALL_OPTION] : []),
                  ]
                : deckingTypeOptions
            }
            name={"deck-type"}
            disabled={archived}
          />
          {renderSpaOrWallWarning()}
          {renderHardwareSection()}
        </>
      ) : (
        <>
          <ManagedSelect
            label={deckTypeLabelCopy}
            isValid={validFields.decking}
            value={deckingType}
            onChange={setDeckingType}
            options={deckingTypeOptions}
            name={"deck-type"}
            disabled={archived}
          />
          {showHardwareDropdown && (
            <ManagedSelect
              label={hardwareLabelCopy}
              isValid={validFields.hardware}
              value={hardwareOption}
              onChange={setHardwareOption}
              name={"hardware-type"}
              options={
                deckingType
                  ? hardwareTypeOptions[deckingType as keyof typeof hardwareTypeOptions]
                  : []
              }
              disabled={archived}
            />
          )}
        </>
      )}
      {hardwareOption && showHardwareDropdown && (
        <ManagedToggle
          label={brassCollarsCopy}
          name={"collars"}
          isValid={validFields.collars}
          onChange={setIncludeBrassCollars}
          checked={includeBrassCollars ?? false}
          disabled={archived}
        />
      )}
      <ManagedToggle
        label={paddingCopy}
        name={"padding"}
        isValid={validFields.padding}
        onChange={setIsRough}
        checked={isRough}
        disabled={archived}
      />
      {isRough && (
        <ManagedSelect
          label={paddingTypeCopy}
          isValid={validFields.paddingType}
          value={paddingType}
          onChange={setPaddingType}
          options={paddingTypeOptions}
          name={"is-rough"}
          disabled={archived}
        />
      )}
    </div>
  );
};

export default Deck;
